import React, { useState } from "react";
import { copyCodeToClipboard } from "utils/misc";
import { ScrollReveal } from "features/whop/whopLanding/components/animations.tsx";
import styled from "styled-components";
import { Role } from "features/whop/whopLanding/WhopLandingPage.tsx";
import {
  SectionContainer,
  HeadingArea,
  SectionHeadline,
  PrimaryButton,
  ButtonCenter,
  CopyIcon,
} from "features/whop/whopLanding/components/shared.tsx";

interface MessageSectionProps {
  role: Role;
}

export const MessageSection: React.FC<MessageSectionProps> = ({ role }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyCodeToClipboard(memberMessage).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (role === "member") {
    return (
      <MemberSectionOuter>
        <MemberSection>
          <ScrollReveal>
            <HeadingArea>
              <Headline>Copy This Message to Your Admin</Headline>
            </HeadingArea>
          </ScrollReveal>
          <ScrollReveal delay={100}>
            <MessageBox>
              <MessageText>{memberMessage}</MessageText>
              <ButtonCenter>
                <CopyButton onClick={handleCopy}>
                  {copied ? <CheckMarkIcon /> : <CopyIcon />}
                  {copied ? "Copied to Clipboard!" : "Copy Message"}
                </CopyButton>
              </ButtonCenter>
            </MessageBox>
          </ScrollReveal>
        </MemberSection>
      </MemberSectionOuter>
    );
  }

  return null;
};

const CheckIcon = () => (
  <svg
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2563EB"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, marginTop: 2 }}
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const CheckMarkIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const DownloadIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

export const memberMessage = `Hey [Admin Name],

I found this automation solution called Cornix that integrates directly with Whop signal groups, and it's free for Whop communities.

It automatically executes signals exactly as posted, including stop-loss and take-profits, and syncs updates in real time.

It doesn't require changing the signal format.

Here's the overview video:
https://youtu.be/s-TGuPticPw

Would you consider adding it to the group?`;

const checklistItems = [
  "How integration works",
  "Security overview",
  "What's required from the admin",
  "Technical setup steps",
  "Direct contact details",
];

const ChecklistBox = styled.div`
  border-radius: 16px;
  padding: 40px;
  border: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"};
  background: ${(p) => (p.theme.isDarkMode ? "#111827" : "#ffffff")};
  box-shadow: 0 2px 12px
    ${(p) => (p.theme.isDarkMode ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.04)")};
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const ChecklistItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 32px;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 20px;
`;

const CheckText = styled.span`
  font-size: clamp(1rem, 2.5vw, 1.25rem);
  color: ${(p) => (p.theme.isDarkMode ? "#9CA3AF" : "#4B5563")};
`;

const DownloadButton = styled(PrimaryButton)`
  @media (min-width: 480px) {
    min-width: 280px;
  }
`;
const MemberSectionOuter = styled.div`
  background: ${(p) => (p.theme.isDarkMode ? "#0d1117" : "#F8FAFC")};
  border-top: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
  border-bottom: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
`;

const MemberSection = styled(SectionContainer).attrs({ maxWidth: "900px" })`
  background: transparent;
`;

const Headline = SectionHeadline;

const MessageBox = styled.div`
  border-radius: 16px;
  padding: 40px;
  border: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(37,99,235,0.3)" : "rgba(37,99,235,0.15)"};
  background: ${(p) => (p.theme.isDarkMode ? "#111827" : "#ffffff")};
  box-shadow: 0 2px 12px
    ${(p) => (p.theme.isDarkMode ? "rgba(0,0,0,0.3)" : "rgba(37,99,235,0.06)")};
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const MessageText = styled.p`
  font-size: clamp(0.95rem, 2.5vw, 1.25rem);
  line-height: 1.7;
  color: ${(p) => (p.theme.isDarkMode ? "#9CA3AF" : "#4B5563")};
  white-space: pre-line;
  margin: 0 0 32px;
`;

const CopyButton = PrimaryButton;

const AdminSection = styled.section`
  padding: 80px 24px;
  border-top: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
  border-bottom: 1px solid
    ${(p) =>
      p.theme.isDarkMode ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"};
  background: ${(p) => (p.theme.isDarkMode ? "#0d1117" : "#F8FAFC")};
  @media (max-width: 768px) {
    padding: 56px 20px;
  }
  @media (max-width: 480px) {
    padding: 40px 16px;
  }
`;

const AdminInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;
